import { useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import { ApiError } from '@mds/shared/lib/api';
import type {
  AdminDashboardDrilldownRow,
  DashboardDrilldownMetric,
} from '@mds/shared/types/reporting';

import Button from '@/components/ui/button';
import EmptyState from '@/components/ui/empty-state';
import Modal from '@/components/ui/modal';
import Pager from '@/components/ui/pager';
import { Cell, Row, Table } from '@/components/ui/table';
import { dateStamp, stamp } from '@mds/shared/lib/dates';
import { formatCents } from '@/lib/money';
import * as reporting from '@/reporting/reporting.api';

/** What each figure is called once it is a list rather than a number. */
const TITLES: Record<DashboardDrilldownMetric, string> = {
  bookings: 'Bookings today',
  collected: 'Taken today',
  outstanding: 'Still unpaid',
  occupied_desks: 'Desks taken',
  upcoming_events: 'Upcoming events',
};

/** The columns each row shape fills. The union is discriminated on `kind`. */
const COLUMNS: Record<DashboardDrilldownMetric, string[]> = {
  bookings: ['Reference', 'Customer', 'Space', 'Status', 'Total', 'Balance'],
  collected: ['Payment', 'Booking', 'Customer', 'Item', 'Recorded', 'Amount'],
  outstanding: ['Reference', 'Customer', 'Space', 'Status', 'Total', 'Balance'],
  occupied_desks: ['Desk', 'Space', 'Booking', 'Customer'],
  upcoming_events: ['Event', 'Date', 'Capacity', 'Confirmed', 'Awaiting payment'],
};

/**
 * The rows behind one dashboard tile.
 *
 * The totals line comes from the response rather than the page: `totals` counts
 * the whole filtered result, so it stays right when the reader pages forward.
 */
export default function DrilldownModal({
  metric,
  date,
  onClose,
}: {
  /** Null keeps the modal closed — the tile that was clicked, once one is. */
  metric: DashboardDrilldownMetric | null;
  date: string;
  onClose: () => void;
}) {
  const [page, setPage] = useState(1);

  const query = useQuery({
    queryKey: ['reports', 'drilldown', metric, date, page],
    queryFn: () => reporting.getDashboardDrilldown(metric as DashboardDrilldownMetric, date, page),
    enabled: metric !== null,
  });

  const close = () => {
    setPage(1);
    onClose();
  };

  const rows = query.data?.data ?? [];
  const totals = query.data?.totals;

  return (
    <Modal
      open={metric !== null}
      onClose={close}
      title={metric ? TITLES[metric] : ''}
      footer={
        <Button variant="quiet" onClick={close}>
          Close
        </Button>
      }
    >
      {!metric ? null : query.isPending ? (
        <p className="m-0 text-base text-ink-muted">Reading the detail…</p>
      ) : query.isError ? (
        <EmptyState
          title="Could not read the detail"
          hint={
            query.error instanceof ApiError
              ? query.error.message
              : 'The server could not be reached.'
          }
        />
      ) : rows.length === 0 ? (
        <EmptyState title="Nothing behind this figure" hint="No records matched for this date." />
      ) : (
        <>
          {totals && (
            <p className="mt-0 mb-4 text-base text-ink-secondary">
              {totals.count.toLocaleString()} {totals.count === 1 ? 'record' : 'records'}
              {totals.amountMinor !== null && ` · ${formatCents(totals.amountMinor)}`}
              {totals.seatCount !== null &&
                ` · ${totals.seatCount.toLocaleString()} confirmed ${
                  totals.seatCount === 1 ? 'seat' : 'seats'
                }`}
            </p>
          )}

          <Table head={COLUMNS[metric]}>
            {rows.map((row) => (
              <DrilldownRow key={row.kind + row.id} row={row} />
            ))}
          </Table>

          <Pager pagination={query.data?.pagination} onPage={setPage} />
        </>
      )}
    </Modal>
  );
}

function DrilldownRow({ row }: { row: AdminDashboardDrilldownRow }) {
  switch (row.kind) {
    case 'booking':
      return (
        <Row>
          <Cell className="font-mono text-md">{row.reference}</Cell>
          <Cell>{row.customerName}</Cell>
          <Cell>
            {row.resourceName}
            <span className="text-ink-muted"> · {row.allocationType}</span>
          </Cell>
          <Cell>{row.status === 'confirmed' ? 'Confirmed' : 'Awaiting payment'}</Cell>
          <Cell className="font-mono text-md">{formatCents(row.totalAmountMinor)}</Cell>
          <Cell className="font-mono text-md">
            {row.balanceAmountMinor > 0 ? formatCents(row.balanceAmountMinor) : 'Settled'}
          </Cell>
        </Row>
      );

    case 'payment':
      return (
        <Row>
          <Cell className="font-mono text-md">{row.reference}</Cell>
          <Cell className="font-mono text-md">{row.sourceReference}</Cell>
          <Cell>{row.customerName}</Cell>
          <Cell>{row.itemName}</Cell>
          <Cell className="font-mono text-md text-ink-muted">{stamp(row.recordedAt)}</Cell>
          <Cell className="font-mono text-md">{formatCents(row.amountMinor)}</Cell>
        </Row>
      );

    case 'desk':
      return (
        <Row>
          <Cell className="font-mono text-md">{row.deskKey}</Cell>
          <Cell>{row.resourceName}</Cell>
          <Cell className="font-mono text-md">{row.bookingReference}</Cell>
          <Cell>{row.customerName}</Cell>
        </Row>
      );

    case 'event':
      return (
        <Row>
          <Cell>{row.title}</Cell>
          <Cell className="font-mono text-md text-ink-muted">{dateStamp(row.eventDate)}</Cell>
          <Cell className="font-mono text-md">{row.capacity}</Cell>
          <Cell className="font-mono text-md">{row.confirmedSeatCount}</Cell>
          <Cell className="font-mono text-md text-ink-muted">{row.paymentPendingSeatCount}</Cell>
        </Row>
      );
  }
}
